import {
  Alert,
  Box,
  Button,
  Container,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import BlockIcon from "@mui/icons-material/Block";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/auth/AuthContext";

/**
 * Generic "wrong role" page. When a doctor signup just failed (the post-
 * registration {@code POST /api/auth/doctor/signup} or token refresh threw),
 * we surface the real error and offer a retry so the user isn't stuck on
 * a generic "you can't access this page" dead end.
 */
export default function UnauthorizedPage() {
  const navigate = useNavigate();
  const { doctorSignupError, retryDoctorSignup, isAuthenticated } = useAuth();

  const showDoctorSignupFailure = Boolean(doctorSignupError) && isAuthenticated;

  return (
    <Container maxWidth="sm" sx={{ py: { xs: 6, md: 10 } }}>
      <Paper variant="outlined" sx={{ p: 5, textAlign: "center" }}>
        <Box
          sx={{
            width: 72,
            height: 72,
            mx: "auto",
            mb: 2,
            borderRadius: "50%",
            bgcolor: "error.light",
            color: "#fff",
            display: "grid",
            placeItems: "center",
          }}
        >
          <BlockIcon fontSize="large" />
        </Box>

        {showDoctorSignupFailure ? (
          <>
            <Typography variant="h5" gutterBottom>
              We couldn't finish your doctor signup
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 2 }}>
              Your account was created, but promoting it to a doctor
              account didn't complete. You can retry the last step below.
            </Typography>
            <Alert severity="error" sx={{ textAlign: "left", mb: 3 }}>
              {doctorSignupError}
            </Alert>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1.5}
              justifyContent="center"
            >
              <Button
                variant="contained"
                startIcon={<RefreshIcon />}
                onClick={retryDoctorSignup}
              >
                Retry doctor signup
              </Button>
              <Button variant="outlined" onClick={() => navigate("/")}>
                Go home
              </Button>
            </Stack>
          </>
        ) : (
          <>
            <Typography variant="h5" gutterBottom>
              You can't access this page
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 3 }}>
              Your account doesn't have the right role to open this section.
              If you think this is a mistake, please contact support.
            </Typography>
            <Stack direction="row" spacing={1.5} justifyContent="center">
              <Button variant="outlined" onClick={() => navigate(-1)}>
                Go back
              </Button>
              <Button variant="contained" onClick={() => navigate("/")}>
                Go home
              </Button>
            </Stack>
          </>
        )}
      </Paper>
    </Container>
  );
}
